import React, { ChangeEventHandler, FocusEventHandler, ReactNode } from 'react';

import clsx from 'clsx';
import Spinner from 'components/Spinner';
import { RenderInputComponentProps } from 'react-autosuggest';

interface SearchInputProps {
  disabled?: boolean;
  autoFocus?: boolean;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  children?: ReactNode;
  isLoading?: boolean;
  onChangeHandler?: ChangeEventHandler<HTMLInputElement>;
  searchText?: string | number | readonly string[];
  searchResults?: any[];
  inputPlaceholder?: string;
  onClearSearch?: React.MouseEventHandler<HTMLElement>;
  inputProps?: RenderInputComponentProps;
}

/**  Search input
 *   source: https://tw-elements.com/docs/standard/forms/search/#icon
 */
export default function SearchInput(props: SearchInputProps) {
  const inputClassNames = clsx(
    'isd-search-input-element dark:focus:border-primary focus:border-primary relative m-0 w-full min-w-0',
    'rounded-lg border border-solid border-gray-200 bg-clip-padding px-3 py-3 pr-14',
    'text-base leading-[1.6] font-normal text-neutral-700 outline-hidden',
    'transition duration-200 ease-in-out focus:z-3 focus:text-neutral-700',
    'focus:shadow-[inset_0_0_0_1px_rgb(0,71,80,0.6)] focus:outline-hidden focus:placeholder:text-neutral-200 dark:placeholder:text-neutral-400',
    props.disabled ? 'bg-neutral-200' : 'bg-transparent'
  );
  return (
    <div className="relative flex w-full items-stretch">
      <label className="relative block flex-auto">
        <input
          type="search"
          placeholder={props.inputPlaceholder || 'Search'}
          value={props.searchText}
          disabled={props.disabled}
          // @ts-ignore
          onChange={props.onChangeHandler}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          autoFocus={props.autoFocus}
          {...props.inputProps}
          className={clsx(inputClassNames, props.inputProps?.className)}
        />
        {!props.isLoading &&
        (!!props.searchText || !!props.searchResults?.length) ? (
          <ClearSearchIcon onClick={props.onClearSearch} />
        ) : (
          <SearchIcon loading={props.isLoading} />
        )}
      </label>
      {props.children}
    </div>
  );
}

const inputIconClassNames = clsx(
  'input-group-text absolute top-1/2 right-1 -translate-y-1/2 transform items-center',
  'rounded-sm px-3 py-1.5 text-center text-base leading-6 font-normal whitespace-nowrap'
);

function SearchIcon({ loading = false }: { loading?: boolean }) {
  return (
    <span
      className={`${inputIconClassNames} text-neutral-700 dark:text-neutral-200`}
      id="basic-addon2"
    >
      {!loading ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-5 w-5"
        >
          <path
            d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
            fillRule="evenodd"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <Spinner />
      )}
    </span>
  );
}

function ClearSearchIcon({
  onClick,
}: {
  onClick?: React.MouseEventHandler<HTMLElement>;
}) {
  return (
    <span
      onClick={onClick}
      className={`${inputIconClassNames} clickable z-10 text-neutral-400`}
      id="basic-addon2"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        stroke="currentColor"
        strokeWidth="1.5"
        className="h-5 w-5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6 18 18 6M6 6l12 12"
          fillRule="evenodd"
          clipRule="evenodd"
        />
      </svg>
    </span>
  );
}
